import { Variant, Product } from "@/types/types";
import { getMainVariant } from "@/utils/product/getMainVariant";
import { removeHtml } from "@/utils/string/removeHtml";
import { isTwoManDelivery } from "@/utils/product/isTwoManDelivery";
import { isMadeToMeasure } from "@/utils/product/isMadeToMeasure";
import { isPerMetre } from "@/utils/product/isPerMetre";
import { isEligibleForFreeReturns } from "@/utils/product/isEligibleForFreeReturns";
import { getReturnsExclusionsMessage } from "@/components/ProductAccordion/components/ProductDeliveryOptionsAccordion/ReturnsExclusions/utils/getReturnsExclusionsMessage";
import { ensureIsActive } from "@/utils/product/ensureIsActive";
import { hasAnyStock } from "@/utils/product/hasAnyStock";
import { isInStoreOnly } from "@/utils/variant/isInStoreOnly.ts";
import { isAvailableToPreOrder } from "@/utils/variant/isAvailableToPreOrder.ts";
import { isFutureRelease } from "@/utils/variant/isFutureRelease.ts";
import { AvailableOption, FulfilmentOptions } from "@/types/prodcatBaseTypes";
import {
  DMS_IDS,
  FREE_DELIVERY_THRESHOLD,
  FREE_CLICK_AND_COLLECT_THRESHOLD,
} from "@/constants/constants";

export type StructuredDataProductGroup = {
  "@type": "ProductGroup";
  "@context": "https://schema.org/";
  name: string;
  url: string;
  productGroupID: string;
  description?: string;

  brand?: {
    "@type": "Brand";
    name: string;
  };
  model?: string;
  category?: string;
  audience?: {
    "@type": "PeopleAudience";
    suggestedGender?: string;
  };
  material?: string[];
  size?: string[];

  variesBy?: string[];
  hasVariant?: StructuredDataProduct[];
  aggregateRating?: {
    "@type": "AggregateRating";
    reviewCount: number;
    ratingValue: number;
  };
};

export type StructuredDataProduct = {
  "@type": "Product";
  "@context"?: "https://schema.org/";
  name: string;
  url: string;
  productId: string;
  image: string;
  description?: string;

  brand?: {
    "@type": "Brand";
    name: string;
  };
  model?: string;
  category?: string;
  audience?: {
    "@type": "PeopleAudience";
    suggestedGender?: string;
  };
  material?: string[];
  size?: string[];
  color?: string;
  offers?: StructuredDataOffer | StructuredDataOffer[];
  aggregateRating?: {
    "@type": "AggregateRating";
    reviewCount: number;
    ratingValue: number;
  };
  pattern?: string;
};

type StructuredDataOffer = {
  "@type": "Offer";
  sku?: string;
  price?: string;
  priceCurrency: string;
  availability?: string;
  url: string;
  priceSpecification?: {
    "@type": "UnitPriceSpecification";
    price: string;
    priceCurrency: string;
    priceType: string;
    valueAddedTaxIncluded: string;
  };
  availabilityStarts?: string;
  gtin13?: string;
  gtin8?: string;
  gtin12?: string;
  hasMerchantReturnPolicy?: MerchantReturnPolicy;
  shippingDetails?: ShippingDetail[];
};

type ShippingDetail = {
  "@type": "OfferShippingDetails";
  shippingDestination: {
    "@type": "DefinedRegion";
    addressCountry: "GB";
  };
  name: string;
  shippingRate: {
    "@type": "MonetaryAmount";
    value: string;
    currency: string;
  };
};

type MerchantReturnPolicy = {
  "@type": "MerchantReturnPolicy";
  applicableCountry: string;
  returnPolicyCategory: string;
  merchantReturnDays: number;
  returnMethod: string;
  returnFees: string;
  additionalProperty?: {
    "@type": "PropertyValue";
    name: string;
    value: string;
  };
  returnShippingFeesAmount?: {
    "@type": "MonetaryAmount";
    value: string;
    currency: string;
  };
};

const returnWithinNumDays = 30;
const returnFeeAmount = "29.95";

export const buildStructuredData = (
  product: Product,
): StructuredDataProductGroup | StructuredDataProduct | object => {
  if (!product.variants || product.variants.length === 0) {
    return {};
  }
  const { variants } = product;

  const colours: Set<string> = new Set(
    variants
      .map((variant: Variant) => variant.differentiators?.colour)
      .filter((c) => c !== undefined && c !== null),
  );

  // Build common data that's shared between Product and ProductGroup

  if (variants.length === 1 || colours.size <= 1) {
    // Single product or single color - return Product type
    const commonData = buildCommonData(product);
    const allOffers = variants.flatMap((variant) =>
      buildOfferTypeData(variant, product),
    );
    if (allOffers.length === 0 || allOffers[0] === undefined) {
      return {};
    }
    const firstOffer: StructuredDataOffer = allOffers[0];
    const mainVariant = getMainVariant(product);

    const data: StructuredDataProduct = {
      "@type": "Product",
      "@context": "https://schema.org/",
      ...commonData,
      name: product.title,
      url: `https://www.johnlewis.com${mainVariant.canonicalURL}`,
      productId: product.aliases.pimProductId,
      image: `https:${mainVariant.images.primary}`,
      offers: variants.length === 1 ? firstOffer : allOffers,
    };

    if (variants.length > 1) {
      const allSizes = getAllUniqueSizes(variants);
      if (allSizes.length > 0) {
        data.size = allSizes;
      }
    }

    // Add material from first variant if available
    if (mainVariant) {
      const materialValues = getMaterialValues(mainVariant);
      if (materialValues.length > 0) {
        data.material = materialValues;
      }
    }

    const patternAttribute = product.attributes?.find(
      (attr) => attr.key.toLowerCase() === "typeofpattern",
    );
    if (patternAttribute && patternAttribute.values.length > 0) {
      data.pattern = patternAttribute.values.join(", ");
    }

    return data;
  } else {
    // Multiple colors - return ProductGroup type
    const hasVariant = buildProductVariants(product, variants);
    const allSizes = getAllUniqueSizes(variants);
    const commonData = buildCommonData(product);
    const data: StructuredDataProductGroup = {
      "@type": "ProductGroup",
      "@context": "https://schema.org/",
      ...commonData,
      name: product.title,
      url: `https://www.johnlewis.com${product.canonicalURL}`,
      productGroupID: product.aliases.pimProductId,

      variesBy: ["color"],
      hasVariant,
    };
    //TODO:Sizes
    if (allSizes.length > 0) {
      data.size = allSizes;
    }
    return data;
  }
};

const stripBrandName = (name: string, brandName: string): string => {
  const regex = new RegExp(`\\b${brandName}\\b`, "i");
  return name.replace(regex, "").trim();
};

const buildCategory = (product: Product): string => {
  const path = product.browsePageHierarchy.slice(1);
  return path.map((p) => p.name).join(" > ");
};

const buildCommonData = (product: Product) => {
  const commonData: { [key: string]: any } = {
    description: removeHtml(product.description),

    brand: {
      "@type": "Brand",
      name: product.brand?.name,
    },
    model: stripBrandName(product.title, product.brand?.name || ""),
    category: buildCategory(product),
  };

  if (product.reviewSummary?.averageRating) {
    commonData.aggregateRating = {
      "@type": "AggregateRating",
      reviewCount: product.reviewSummary.numberOfReviews,
      ratingValue: product.reviewSummary.averageRating,
    };
  }
  return commonData;
};

const buildProductVariants = (
  product: Product,
  variants: Variant[],
): StructuredDataProduct[] => {
  // Group variants by color
  const variantsByColor = variants.reduce(
    (acc: Record<string, Variant[]>, variant) => {
      const color = variant.differentiators?.colour || "default";
      if (!acc[color]) {
        acc[color] = [];
      }
      acc[color].push(variant);
      return acc;
    },
    {},
  );

  // Create a Product for each color group
  return Object.entries(variantsByColor).map(([color, colorVariants]) => {
    const mainVariant = colorVariants[0];
    const canonicalURL = mainVariant?.canonicalURL || product.canonicalURL;
    const offers = colorVariants.map((variant) =>
      buildOfferTypeData(variant, product),
    );

    const productData: StructuredDataProduct = {
      "@type": "Product",
      name: `${product.title}, ${color}`,
      url: `https://www.johnlewis.com${canonicalURL}`,
      productId: product.aliases.pimProductId,
      image: `https:${mainVariant?.images.primary}`,

      color: color,
      offers: offers,
    };

    // Add material from main variant if available
    if (mainVariant) {
      const materialValues = getMaterialValues(mainVariant);
      if (materialValues.length > 0) {
        productData.material = materialValues;
      }
    }

    const patternAttribute = product.attributes?.find(
      (attr) => attr.key.toLowerCase() === "typeofpattern",
    );
    if (patternAttribute && patternAttribute.values.length > 0) {
      productData.pattern = patternAttribute.values.join(", ");
    }

    return productData;
  });
};

const buildAvailability = (
  variant: Variant,
): { availability: string; availabilityStarts?: string } => {
  if (
    variant.availability?.availableToOrder &&
    !variant.availability.preOrder
  ) {
    return { availability: "https://schema.org/InStock" };
  } else if (
    !variant.availability?.availableToOrder &&
    variant.availability?.unavailableReason == "inStoreOnly"
  ) {
    return { availability: "https://schema.org/InStoreOnly" };
  } else if (
    variant.availability?.preOrder &&
    variant.availability?.availableToOrder
  ) {
    return {
      availability: "https://schema.org/PreOrder",
      availabilityStarts:
        variant.availability?.preOrder.streetDate || undefined,
    };
  } else if (
    ["outOfStock", "notYetAvailable"].includes(
      variant.availability?.unavailableReason ?? "",
    )
  ) {
    return { availability: "https://schema.org/OutOfStock" };
  } else {
    return { availability: "https://schema.org/Discontinued" };
  }
};

const buildMerchantReturnPolicyData = (
  variant: Variant,
  product: Product,
): MerchantReturnPolicy | null => {
  const isActiveProduct = !!ensureIsActive(product);

  const isOutOfStockSingleSku =
    isActiveProduct && !hasAnyStock(product) && product.variants?.length === 1;

  const isVariantAvailable =
    isInStoreOnly(variant) ||
    isAvailableToPreOrder(variant) ||
    isFutureRelease(variant);

  const hideDeliveryMessage =
    !isActiveProduct || isOutOfStockSingleSku || isVariantAvailable;

  const is2ManDelivery = isTwoManDelivery(product);

  const showFreeReturns =
    !is2ManDelivery &&
    !isMadeToMeasure(product) &&
    !isPerMetre(product) &&
    isEligibleForFreeReturns(product);

  const returnsExclusionsMessage = getReturnsExclusionsMessage(
    product.browsePageHierarchy,
    product.sellingProductType || "",
  );

  const showFreeReturnsWithRestrictions =
    variant.sellingRestrictions?.age?.bladedArticle ||
    !!returnsExclusionsMessage;

  const returnsRestrictionsMessage = variant.sellingRestrictions?.age
    ?.bladedArticle
    ? "You can return this item to a John Lewis shop, or by Royal Mail. You cannot return bladed items to Waitrose stores, or any other return locations."
    : returnsExclusionsMessage?.message;

  if (!hideDeliveryMessage) {
    if (showFreeReturns) {
      if (showFreeReturnsWithRestrictions) {
        return {
          "@type": "MerchantReturnPolicy",
          applicableCountry: "GB",
          returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
          merchantReturnDays: returnWithinNumDays,
          returnMethod: "https://schema.org/ReturnByMail",
          returnFees: "https://schema.org/FreeReturn",
          ...(returnsRestrictionsMessage && {
            additionalProperty: {
              "@type": "PropertyValue",
              name: "Return Restriction",
              value: returnsRestrictionsMessage,
            },
          }),
        };
      } else {
        return {
          "@type": "MerchantReturnPolicy",
          applicableCountry: "GB",
          returnPolicyCategory:
            "https://schema.org/MerchantReturnFiniteReturnWindow",
          merchantReturnDays: returnWithinNumDays,
          returnMethod: "https://schema.org/ReturnByMail",
          returnFees: "https://schema.org/FreeReturn",
        };
      }
    } else if (is2ManDelivery) {
      return {
        "@type": "MerchantReturnPolicy",
        applicableCountry: "GB",
        returnPolicyCategory:
          "https://schema.org/MerchantReturnFiniteReturnWindow",
        merchantReturnDays: returnWithinNumDays,
        returnMethod: "https://schema.org/ReturnByMail",
        returnFees: "https://schema.org/ReturnShippingFees",
        returnShippingFeesAmount: {
          "@type": "MonetaryAmount",
          value: returnFeeAmount,
          currency: "GBP",
        },
      };
    }
  }

  return null;
};

const buildOfferTypeData = (
  variant: Variant,
  product: Product,
): StructuredDataOffer => {
  const data: StructuredDataOffer = {
    "@type": "Offer",
    sku: variant.aliases.skuId,
    price: variant.price?.value,
    priceCurrency: "GBP",
    url: `https://www.johnlewis.com${variant.canonicalURL}`,
  };

  const { consumerBarcodes } = variant;

  const { availability, availabilityStarts } = buildAvailability(variant);
  data.availability = availability;
  if (availabilityStarts) {
    data.availabilityStarts = availabilityStarts;
  }

  if (consumerBarcodes != undefined) {
    if (consumerBarcodes?.ean13?.length > 0) {
      data.gtin13 = consumerBarcodes.ean13[0];
    }
    if (consumerBarcodes?.ean8?.length > 0) {
      data.gtin8 = consumerBarcodes.ean8[0];
    }
    if (consumerBarcodes?.upc12?.length > 0) {
      data.gtin12 = consumerBarcodes.upc12[0];
    }
  }

  const merchantReturnPolicyData = buildMerchantReturnPolicyData(
    variant,
    product,
  );
  if (merchantReturnPolicyData) {
    data.hasMerchantReturnPolicy = merchantReturnPolicyData;
  }
  const { fulfilmentOptions } = product;
  if (
    fulfilmentOptions != undefined &&
    fulfilmentOptions?.available.length > 0 &&
    hasValidDMSids(fulfilmentOptions)
  ) {
    if (!isTwoManDelivery(product)) {
      data.shippingDetails = fulfilmentOptions.available?.map((detail) => {
        const isFreeShipping = isFreeShippingEligibleMethod(
          detail,
          variant.price?.value ?? "0",
        );

        return {
          "@type": "OfferShippingDetails",
          shippingRate: {
            "@type": "MonetaryAmount",
            value: isFreeShipping ? "0.00" : detail.price.value,
            currency: "GBP",
          },
          name: `${isFreeShipping ? "Free " : ""}${detail.headline}`,
          shippingDestination: {
            "@type": "DefinedRegion",
            addressCountry: "GB",
          },
        };
      });
    } else {
      data.shippingDetails = fulfilmentOptions.available
        .filter(
          (option) =>
            option.aliases.dmsId === DMS_IDS.TwoPersonDelivery.toString(),
        )
        .map((detail) => {
          return {
            "@type": "OfferShippingDetails",
            shippingRate: {
              "@type": "MonetaryAmount",
              value: detail.price.value,
              currency: "GBP",
            },
            name: "Standard Large Item Delivery",
            shippingDestination: {
              "@type": "DefinedRegion",
              addressCountry: "GB",
            },
          };
        });
    }
  }

  return data;
};

const getAllUniqueSizes = (variants: Variant[]): string[] => {
  const sizes = new Set<string>();

  variants.forEach((variant) => {
    const size = variant.differentiators?.size;
    if (size) {
      sizes.add(size);
    }
  });

  return Array.from(sizes);
};

const getMaterialValues = (variant: Variant): string[] => {
  const materialAttribute = variant.attributes?.find(
    (attr) => attr.key.toLowerCase() === "material",
  );
  return materialAttribute?.values || [];
};
const isFreeShippingEligibleMethod = (
  fulfilmentOption: AvailableOption,
  value: string,
): boolean => {
  return (
    ([
      DMS_IDS.OnePersonDelivery.toString(),
      DMS_IDS.SupplierDelivery.toString(),
      DMS_IDS.M2MDelivery.toString(),
      DMS_IDS.SupplierDeliveryCondor.toString(),
    ].includes(fulfilmentOption.aliases.dmsId) &&
      parseFloat(value) >= FREE_DELIVERY_THRESHOLD) ||
    ([
      DMS_IDS.ClickAndCollect.toString(),
      DMS_IDS.LocalCollectionNetwork.toString(),
    ].includes(fulfilmentOption.aliases.dmsId) &&
      parseFloat(value) >= FREE_CLICK_AND_COLLECT_THRESHOLD)
  );
};

const hasValidDMSids = (fulfilmentOptions: FulfilmentOptions): boolean => {
  return fulfilmentOptions.available.some((option) =>
    [
      DMS_IDS.OnePersonDelivery.toString(),
      DMS_IDS.SupplierDelivery.toString(),
      DMS_IDS.M2MDelivery.toString(),
      DMS_IDS.SupplierDeliveryCondor.toString(),
      DMS_IDS.TwoPersonDelivery.toString(),
      DMS_IDS.ClickAndCollect.toString(),
    ].includes(option.aliases.dmsId),
  );
};

export default buildStructuredData;
